import { useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import type { PersonDetail } from '@/api/persons.api';
import apiClient from '@/api/client';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/api/queryKeys';
import { useAuthStore } from '@/store/useAuthStore';
import { useTreeStore } from '@/store/useTreeStore';
import { useRelationships } from '@/hooks/useRelationships';
import { useUploadUrl } from '@/hooks/useUploadUrl';
import { OriginsChart } from '../OriginsChart';
import { CareerSection } from './CareerSection';
import { FactBadge, useFactConfidence } from '../FactConfidence';

// ─── Gender-Badge ───────────────────────────────────────────────────────────

const GENDER_LABELS: Record<string, string> = {
  male: 'Männlich',
  female: 'Weiblich',
  other: 'Divers',
  unknown: 'Unbekannt',
};

const GENDER_COLORS: Record<string, string> = {
  male: 'bg-blue-100 text-blue-700',
  female: 'bg-pink-100 text-pink-700',
  other: 'bg-green-100 text-green-700',
  unknown: 'bg-slate-100 text-slate-600',
};

// ─── Partnerschafts-Labels ──────────────────────────────────────────────────

const PARTNERSHIP_TYPE_LABELS: Record<string, string> = {
  married: 'Verheiratet',
  partnership: 'Partnerschaft',
  divorced: 'Geschieden',
  separated: 'Getrennt',
};

const PARTNERSHIP_COLORS: Record<string, string> = {
  married: 'bg-blue-100 text-blue-700',
  partnership: 'bg-violet-100 text-violet-700',
  divorced: 'bg-slate-100 text-slate-600',
  separated: 'bg-slate-100 text-slate-600',
};

// ─── URL-Erkennung in Notizen ───────────────────────────────────────────────

function linkifyText(text: string) {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, i) => {
    if (urlRegex.test(part)) {
      // Reset lastIndex after test
      urlRegex.lastIndex = 0;
      return (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline hover:text-blue-800"
        >
          {part}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

// ─── Info-Zeile ─────────────────────────────────────────────────────────────

function InfoRow({
  label,
  children,
  badge,
}: {
  label: string;
  children: React.ReactNode;
  /** Kleines Abzeichen rechts, z. B. der Sicherheitsgrad der Angabe. */
  badge?: React.ReactNode;
}) {
  if (!children) return null;
  return (
    <div className="flex gap-3 py-2">
      <dt className="w-24 shrink-0 text-xs font-medium text-slate-400">{label}</dt>
      <dd className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-slate-700">
        {children}
        {badge}
      </dd>
    </div>
  );
}

// ─── OverviewTab ────────────────────────────────────────────────────────────

interface OverviewTabProps {
  person: PersonDetail;
}

export function OverviewTab({ person }: OverviewTabProps) {
  const canManage = useAuthStore((s) => s.hasPermission('persons.manage'));
  const setSelectedPerson = useTreeStore((s) => s.setSelectedPerson);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { data: relationships } = useRelationships(person.id);
  // Sicherheitsgrad je Angabe – aus den Belegen zusammengefasst.
  const confidenceByFact = useFactConfidence(person);
  const profilePhotoUrl = useUploadUrl(person.profileMedia?.filename);

  // ─── Foto-Upload ────────────────────────────────────────────────────────
  // Der Avatar-Klick bedeutet "Profilbild ändern": Das Backend setzt nur
  // dann automatisch ein Profilbild, wenn noch keins existiert – ein
  // vorhandenes wird daher explizit auf das neue Bild umgestellt.

  const handlePhotoUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('file', file);

      try {
        const { data: media } = await apiClient.post<{ id: string }>(
          `/persons/${person.id}/media`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );

        if (person.profileMedia) {
          await apiClient.patch(`/persons/${person.id}`, { profile_media_id: media.id });
        }

        toast.success('Profilbild aktualisiert.');
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.detail(person.id) });
        queryClient.invalidateQueries({ queryKey: ['media', person.id] });
        // Baum zeigt das Profilbild ebenfalls an
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tree.all });
      } catch {
        toast.error('Foto konnte nicht hochgeladen werden.');
      }

      // Input zurücksetzen
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [person.id, person.profileMedia, queryClient],
  );

  // ─── Anzeige-Daten ──────────────────────────────────────────────────────

  const fullName = [person.title, person.firstName, person.lastName].filter(Boolean).join(' ');
  const genderKey = person.gender ?? 'unknown';
  const birthInfo = [person.birthDateText, person.birthPlace].filter(Boolean).join(', ');
  const deathInfo = [person.deathDateText, person.deathPlace].filter(Boolean).join(', ');

  return (
    <div className="space-y-5">
      {/* Profilfoto */}
      <div className="flex flex-col items-center pt-2">
        <button
          onClick={() => canManage && fileInputRef.current?.click()}
          className={`group relative h-20 w-20 overflow-hidden rounded-full border-2 border-slate-200
                     bg-slate-100 transition-colors ${canManage ? 'hover:border-blue-400 cursor-pointer' : 'cursor-default'}`}
          title={canManage ? 'Klicken zum Hochladen' : undefined}
        >
          {person.profileMedia && profilePhotoUrl !== undefined ? (
            <img
              src={profilePhotoUrl}
              alt={fullName}
              className="h-full w-full object-cover"
            />
          ) : (
            <svg className="mx-auto mt-4 h-10 w-10 text-slate-300" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          )}
          {/* Overlay bei Hover (nur Admin) */}
          {canManage && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover:opacity-100">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z" />
              </svg>
            </div>
          )}
        </button>
        {canManage && (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handlePhotoUpload}
          />
        )}
      </div>

      {/* Name + Badge */}
      <div className="text-center">
        <h3 className="text-lg font-semibold text-slate-900">{fullName}</h3>
        {person.birthName && (
          <p className="text-sm text-slate-500">geb. {person.birthName}</p>
        )}
        <span
          className={`mt-1.5 inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${GENDER_COLORS[genderKey] ?? GENDER_COLORS.unknown}`}
        >
          {GENDER_LABELS[genderKey] ?? genderKey}
        </span>
      </div>

      {/* Trennlinie */}
      <hr className="border-slate-200" />

      {/* Details */}
      <dl className="px-1">
        {birthInfo && (
          <InfoRow label="Geburt" badge={<FactBadge fact="birthDate" confidenceByFact={confidenceByFact} />}>
            <span>{person.birthDateText && `* ${person.birthDateText}`}</span>
            {person.birthPlace && (
              <span className="text-slate-500">{person.birthDateText ? ', ' : ''}{person.birthPlace}</span>
            )}
          </InfoRow>
        )}

        {!person.isLiving && deathInfo && (
          <InfoRow label="Tod" badge={<FactBadge fact="deathDate" confidenceByFact={confidenceByFact} />}>
            <span>{person.deathDateText && `† ${person.deathDateText}`}</span>
            {person.deathPlace && (
              <span className="text-slate-500">{person.deathDateText ? ', ' : ''}{person.deathPlace}</span>
            )}
          </InfoRow>
        )}

        {person.isLiving && (
          <InfoRow label="Status">
            <span className="inline-flex items-center gap-1.5 text-green-600">
              <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
              Lebend
            </span>
          </InfoRow>
        )}

      </dl>

      {/* Laufbahn (ersetzt die frühere "Beruf"-Zeile; das alte Freitextfeld
          erscheint dort als Fallback-Station, solange keine Stationen erfasst sind) */}
      <hr className="border-slate-200" />
      <div className="px-1">
        {/* key: Formular-State darf einen Personenwechsel nicht überleben –
            sonst landet eine angefangene Station bei der falschen Person. */}
        <CareerSection key={person.id} person={person} />
      </div>

      {/* Partnerschaften */}
      {relationships && relationships.length > 0 && (
        <>
          <hr className="border-slate-200" />
          <div className="px-1">
            <h4 className="mb-2 text-xs font-medium text-slate-400">Partnerschaften</h4>
            <div className="space-y-2">
              {relationships.map((rel) => {
                const isP1 = rel.person1.id === person.id;
                const partner = isP1 ? rel.person2 : rel.person1;
                const partnerName = `${partner.firstName} ${partner.lastName ?? ''}`.trim();
                const typeColor = PARTNERSHIP_COLORS[rel.type] ?? PARTNERSHIP_COLORS.separated;
                const formatDate = (d: string | null) => {
                  if (!d) return null;
                  try {
                    const date = new Date(d);
                    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
                  } catch { return null; }
                };

                return (
                  <div key={rel.id} className="rounded-lg border border-slate-200 bg-slate-50/50 p-2.5">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => setSelectedPerson(partner.id)}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800"
                      >
                        {partnerName}
                      </button>
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${typeColor}`}>
                        {PARTNERSHIP_TYPE_LABELS[rel.type] ?? rel.type}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-slate-500">
                      {rel.startDate && (
                        <span>seit {formatDate(rel.startDate)}</span>
                      )}
                      {rel.endDate && (
                        <span>bis {formatDate(rel.endDate)}</span>
                      )}
                      {rel.startPlace && (
                        <span>{rel.startPlace}</span>
                      )}
                    </div>
                    {rel.notes && (
                      <p className="mt-1 text-xs text-slate-400">{rel.notes}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Herkunfts-Diagramm */}
      <>
        <hr className="border-slate-200" />
        <div className="px-1">
          <OriginsChart personId={person.id} />
        </div>
      </>

      {/* Notizen */}
      {person.notes && (
        <>
          <hr className="border-slate-200" />
          <div className="px-1">
            <h4 className="mb-1.5 text-xs font-medium text-slate-400">Notizen</h4>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-600">
              {linkifyText(person.notes)}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
